import { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { getArticleBySlug } from '@/mocks/articles';
import { MAIN_SITE_URL } from '@/mocks/categories';

const SITE_URL = (typeof import.meta !== 'undefined' && import.meta.env?.VITE_SITE_URL) || 'https://vigorbuyspread.skin';

function buildArticleJsonLd(article: ReturnType<typeof getArticleBySlug> & {}) {
  if (!article) return '';
  const url = `${SITE_URL}/${article.slug}`;
  return JSON.stringify({
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.metaDescription,
    url,
    datePublished: '2026-07-22',
    dateModified: '2026-07-22',
    author: {
      '@type': 'Organization',
      name: 'Vigorbuy Spreadsheet Guide',
      url: SITE_URL,
    },
    publisher: {
      '@type': 'Organization',
      name: 'Vigorbuy Spreadsheet Guide',
      url: SITE_URL,
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': url,
    },
  });
}

export default function Article() {
  const location = useLocation();
  const slug = location.pathname.replace(/^\//, '');
  const articleContent = getArticleBySlug(slug);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [slug]);

  if (!articleContent) {
    return (
      <main className="min-h-screen bg-background-50 flex items-center justify-center">
        <title>Article Not Found - Vigorbuy Spreadsheet Guide</title>
        <div className="text-center px-4">
          <h1 className="font-heading text-2xl font-bold text-foreground-900 mb-3">Article Not Found</h1>
          <p className="text-foreground-600 mb-5">The article you are looking for does not exist.</p>
          <Link to="/" className="px-6 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-semibold hover:bg-primary-600 transition-all duration-300 whitespace-nowrap cursor-pointer">
            Back to Homepage
          </Link>
        </div>
      </main>
    );
  }

  const jsonLd = buildArticleJsonLd(articleContent);

  return (
    <main className="min-h-screen bg-background-50">
      <title>{articleContent.title}</title>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: jsonLd }}></script>

      <nav className="sticky top-0 z-50 bg-background-50/95 backdrop-blur-md border-b border-background-200/70">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2 cursor-pointer">
              <span className="font-heading text-xl font-bold text-foreground-900 whitespace-nowrap">
                Vigorbuy Spreadsheet Guide
              </span>
            </Link>
            <div className="flex items-center gap-3 md:gap-4">
              <Link to="/" className="hidden sm:inline-flex text-sm text-foreground-600 hover:text-primary-600 transition-colors whitespace-nowrap cursor-pointer">
                <i className="ri-arrow-left-line mr-1"></i>
                Back to Home
              </Link>
              <a
                href={MAIN_SITE_URL}
                target="_blank"
                rel="noopener nofollow"
                className="px-4 py-2 md:px-5 md:py-2 bg-primary-500 text-background-50 rounded-full text-xs md:text-sm font-semibold hover:bg-primary-600 transition-all duration-300 whitespace-nowrap cursor-pointer"
              >
                Buy It Now
              </a>
            </div>
          </div>
        </div>
      </nav>

      <article className="py-10 md:py-16">
        <div className="max-w-3xl mx-auto px-4 md:px-6">
          <div className="mb-10">
            <div className="flex items-center gap-2 text-xs text-foreground-500 mb-4">
              <Link to="/" className="hover:text-primary-600 transition-colors cursor-pointer">Home</Link>
              <span>/</span>
              <span className="text-primary-600 truncate max-w-[200px]">{articleContent.title.length > 40 ? articleContent.title.substring(0, 40) + '...' : articleContent.title}</span>
            </div>

            <div className="w-full h-48 md:h-64 rounded-xl overflow-hidden mb-6">
              <img
                src={articleContent.imageUrl}
                alt={articleContent.title}
                className="w-full h-full object-cover object-center"
              />
            </div>

            <h1 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-900 leading-tight mb-4">
              {articleContent.title}
            </h1>
            <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
              {articleContent.subtitle}
            </p>
          </div>

          <div className="text-sm md:text-base text-foreground-700 leading-relaxed space-y-5 mb-10">
            {articleContent.intro.split('\n\n').filter(p => p.trim()).map((paragraph, idx) => (
              <p key={idx}>{paragraph.trim()}</p>
            ))}
          </div>

          <div className="mb-8 p-5 bg-primary-50/60 rounded-xl border border-primary-200/50">
            <p className="text-sm text-foreground-700">
              <strong className="text-primary-700">Quick Navigation:</strong> This article covers {articleContent.sections.length} key sections. Scroll through to learn everything you need or jump to the section most relevant to your needs.
            </p>
          </div>

          {articleContent.sections.map((section, idx) => (
            <section key={idx} className="mb-10">
              <h2 className="font-heading text-lg md:text-xl font-bold text-foreground-900 mb-4">
                {section.heading}
              </h2>
              <div className="text-sm md:text-base text-foreground-700 leading-relaxed space-y-4">
                {section.content.split('\n\n').filter(p => p.trim()).map((paragraph, pIdx) => (
                  <p key={pIdx}>{paragraph.trim()}</p>
                ))}
              </div>
            </section>
          ))}

          <section className="mb-10">
            <h2 className="font-heading text-lg md:text-xl font-bold text-foreground-900 mb-4">
              Final Thoughts
            </h2>
            <div className="text-sm md:text-base text-foreground-700 leading-relaxed space-y-4">
              {articleContent.conclusion.split('\n\n').filter(p => p.trim()).map((paragraph, idx) => (
                <p key={idx}>{paragraph.trim()}</p>
              ))}
            </div>
          </section>

          <div className="mt-12 p-6 md:p-8 bg-accent-50/60 rounded-2xl border border-accent-200/50 text-center">
            <h3 className="font-heading text-lg md:text-xl font-bold text-foreground-900 mb-3">
              Ready to Explore Vigorbuy Spreadsheet?
            </h3>
            <p className="text-sm text-foreground-600 mb-5 max-w-lg mx-auto">
              Visit our main store to discover the full range of products and start your journey today.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <a
                href={MAIN_SITE_URL}
                target="_blank"
                rel="noopener nofollow"
                className="px-6 py-2.5 md:px-8 md:py-3 bg-accent-500 text-background-50 rounded-full text-sm md:text-base font-semibold hover:bg-accent-600 transition-all duration-300 whitespace-nowrap cursor-pointer"
              >
                Visit Vigorbuy Main Store
              </a>
              <Link
                to="/"
                className="px-6 py-2.5 md:px-8 md:py-3 bg-background-100 text-foreground-700 rounded-full text-sm md:text-base font-semibold hover:bg-background-200 transition-all duration-300 whitespace-nowrap cursor-pointer"
              >
                Back to Homepage
              </Link>
            </div>
          </div>

          <div className="mt-8 p-5 bg-background-100 rounded-xl border border-background-200/50">
            <p className="text-xs text-foreground-500">
              <strong className="text-foreground-700">Article Navigation:</strong>{' '}
              <Link to="/" className="text-primary-600 hover:text-primary-700 underline cursor-pointer">Return to Vigorbuy Spreadsheet Guide Homepage</Link>
              {' '}| Visit our{' '}
              <a href={MAIN_SITE_URL} target="_blank" rel="noopener nofollow" className="text-primary-600 hover:text-primary-700 underline cursor-pointer">main store</a>
              {' '}for the complete product catalog.
            </p>
          </div>
        </div>
      </article>

      <footer className="py-8 bg-secondary-500 text-background-50">
        <div className="max-w-6xl mx-auto px-4 md:px-6 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-xs text-background-50/50">
            &copy; {new Date().getFullYear()} Vigorbuy Spreadsheet Guide. All rights reserved.
          </p>
          <Link to="/" className="text-xs text-background-50/70 hover:text-background-50 transition-colors cursor-pointer">
            Back to Homepage
          </Link>
        </div>
      </footer>
    </main>
  );
}